

import { useState } from "react";
import { mastersApi } from "@/lib/api";

interface VenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVenueCreated: (venueId: number, venueName: string) => void;
}

export default function VenueModal({ isOpen, onClose, onVenueCreated }: VenueModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    capacity: "",
    price_per_plate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = "Venue name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (formData.capacity && (isNaN(parseInt(formData.capacity)) || parseInt(formData.capacity) <= 0)) {
      newErrors.capacity = "Capacity must be a positive number";
    }
    if (formData.price_per_plate && (isNaN(parseFloat(formData.price_per_plate)) || parseFloat(formData.price_per_plate) < 0)) {
      newErrors.price_per_plate = "Price must be a valid number";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await mastersApi.venues.create({
        name: formData.name,
        location: formData.location,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        price_per_plate: formData.price_per_plate ? parseFloat(formData.price_per_plate) : null,
      });

      const newVenue = response.data?.data || response.data;
      onVenueCreated(newVenue.id, newVenue.name);
      setFormData({ name: "", location: "", capacity: "", price_per_plate: "" });
      onClose();
    } catch (error) {
      setErrors({ submit: "Failed to create venue" });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-stone-800">Add New Venue</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 flex items-center justify-center"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Venue Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Grand Ballroom"
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.name
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Location *</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Downtown, City Center"
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.location
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.location && <p className="text-xs text-rose-500 mt-1">{errors.location}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Capacity (Optional)</label>
            <input
              type="number"
              min="0"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              placeholder="e.g., 500"
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.capacity
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.capacity && <p className="text-xs text-rose-500 mt-1">{errors.capacity}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Price Per Plate (Optional)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price_per_plate}
              onChange={(e) => setFormData({ ...formData, price_per_plate: e.target.value })}
              placeholder="e.g., 500"
              className={`w-full px-3 py-2 rounded-lg border text-sm transition-colors outline-none ${
                errors.price_per_plate
                  ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
                  : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
              }`}
            />
            {errors.price_per_plate && <p className="text-xs text-rose-500 mt-1">{errors.price_per_plate}</p>}
          </div>

          {errors.submit && <p className="text-xs text-rose-500">{errors.submit}</p>}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg gold-gradient text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Venue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
